import React, { FC } from 'react';

import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { LocationDescriptor } from 'history';

import { MATERIAL_ICON_CAMPAIGNS, MATERIAL_ICON_CHEVRON_RIGHT } from '../Button/Icon';

import './Badge.scss';
import { useTranslation } from 'react-i18next';
import { BadgeEnum, mapBadge } from './badgeMapper';

export type BadgeProps = {
    readonly title: string;
    readonly badgeKey?: (typeof BadgeEnum)[keyof typeof BadgeEnum];
    readonly className?: string;
    readonly link?: LocationDescriptor;
};

type BadgeType = 'info' | 'warning' | 'standing-order' | 'campaign-available';

type OwnProps = {
    readonly type: BadgeType;
};

const Badge: FC<BadgeProps & OwnProps> = ({ type, title, badgeKey, className, link }) => {
    const fullClassName = classNames('gild', 'badge', 'is-fullwidth', toShape(type), toTheme(type), className);
    const { t } = useTranslation();
    const mappedBadgeKey = mapBadge(BadgeEnum[badgeKey ?? '']);

    const children = (
        <>
            {type === 'campaign-available' && <i className="material-icons">{MATERIAL_ICON_CAMPAIGNS}</i>}
            <span className="badge-text">
                {t(`components.badges.${mappedBadgeKey}`, {
                    defaultValue: title
                })}
            </span>
            {type === 'campaign-available' && <i className="material-icons">{MATERIAL_ICON_CHEVRON_RIGHT}</i>}
        </>
    );

    if (link) {
        return (
            <Link className={fullClassName} to={link}>
                {children}
            </Link>
        );
    }

    return <div className={fullClassName}>{children}</div>;
};

function toShape(badgeType: BadgeType): string {
    switch (badgeType) {
        case 'campaign-available':
        case 'standing-order':
        case 'warning':
            return 'shape-pill';
        case 'info':
        default:
            return 'shape-flag';
    }
}

function toTheme(badgeType: BadgeType): string {
    switch (badgeType) {
        case 'campaign-available':
            return 'theme-campaign';
        case 'standing-order':
            return 'theme-standingorder';
        case 'warning':
            return 'theme-warning';
        case 'info':
        default:
            return 'theme-positive';
    }
}

export const InfoBadge: FC<BadgeProps> = props => <Badge {...props} type="info" />;

export const CampaignBadge: FC<BadgeProps> = props => <Badge {...props} type="campaign-available" />;

export const WarningBadge: FC<BadgeProps> = props => <Badge {...props} type="warning" />;

export const StandingOrderBadge: FC<BadgeProps> = props => <Badge {...props} type="standing-order" />;
